import { BookmarkCheck, CircleAlert, CircleCheckBig } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';

import { useMemberSession } from '@/auth/auth-provider';

import { useMyMeetList } from './api';
import { fullName, initialsOf, meetListCountLabel, roleAtCompany } from './constants';

/**
 * `MEM-013` AC-4 — the "Your meet list" module on an event's detail screen.
 *
 * ## Transcribed from `Member App.dc.html`, the event screen
 *
 * A `--purple-50` panel with a `1px --purple-100` border and `--radius-lg`; the heading row is a
 * `bookmark-check` glyph and "Your meet list" at `font-brand 700/var(--text-sm)` in
 * `--brand-primary`, with the count badge pushed right — `font-brand 700/var(--text-2xs)`, white
 * on `--brand-primary`, `--radius-pill`. Then an 8px-gapped column of rows: a 34px initials tile,
 * the name at `font-brand 700/var(--text-sm)`, the role line at `var(--text-2xs)`, and a
 * `check-circle-2` in `--brand-accent` on the right.
 *
 * ## Where it renders, which is the AC
 *
 * **Above Who's coming**, and only when the member has at least one entry. `event-detail-page.tsx`
 * places it; this component returns `null` for an empty list rather than drawing an empty panel,
 * so "at least one entry" is a property of the component and not a condition a caller has to
 * remember.
 *
 * ## The cancelled entry
 *
 * `MEM-013` AC-5 marks rather than deletes, and the mark has to be legible: a saved member who
 * cancelled keeps their row, loses the check, and carries the API's sentence. The alternative the
 * story rejects — silent removal — *"leaves the member looking for somebody at an event they are
 * no longer coming to"*.
 */
export function MeetListPanel({ eventId }: { readonly eventId: string }): React.ReactElement | null {
  const { session } = useMemberSession();
  const query = useMyMeetList(eventId, { enabled: session !== null });
  const meetList = query.data?.meetList ?? null;

  // `null` covers both "not a registrant" and "saved nobody". Neither draws a panel: `MEM-013`
  // AC-4 makes the module conditional on having an entry, and an empty "Your meet list" would be
  // an instruction rather than the plan it is supposed to be.
  if (meetList === null || meetList.entries.length === 0) return null;

  return (
    <section
      className="space-y-2.5 rounded-lg border border-brand-100 bg-brand-50 p-3.5"
      aria-labelledby="meet-list-heading"
    >
      <div className="flex items-center justify-between gap-3">
        <h2
          id="meet-list-heading"
          className="flex items-center gap-1.5 font-brand text-sm font-bold text-brand"
        >
          <BookmarkCheck className="h-4 w-4" aria-hidden />
          Your meet list
        </h2>
        <span
          className="rounded-pill bg-brand px-2.5 py-[3px] font-brand text-2xs font-bold text-white"
          aria-label={meetListCountLabel(meetList.entries.length)}
        >
          {meetList.entries.length}
        </span>
      </div>

      <ul className="space-y-2">
        {meetList.entries.map((entry) => {
          const line = roleAtCompany(entry);
          return (
            <li key={entry.attendeeRef}>
              <Link
                to={`/app/events/${encodeURIComponent(eventId)}/attendees/${encodeURIComponent(entry.attendeeRef)}`}
                className="flex items-center gap-2.5"
              >
                <span
                  className="inline-flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-pill bg-brand-200 font-brand text-xs font-bold text-brand-700"
                  aria-hidden
                >
                  {initialsOf(entry)}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block font-brand text-sm font-bold text-foreground">
                    {fullName(entry)}
                  </span>
                  {entry.stillAttending ? (
                    line === null ? null : (
                      <span className="block text-2xs text-muted-foreground">{line}</span>
                    )
                  ) : (
                    // The mark. The API supplies the sentence so the wording is stated once.
                    <span className="block text-2xs font-medium text-muted-foreground">
                      {entry.notice}
                    </span>
                  )}
                </span>
                {entry.stillAttending ? (
                  <CircleCheckBig className="h-[15px] w-[15px] shrink-0 text-brand-accent" aria-hidden />
                ) : (
                  <CircleAlert className="h-[15px] w-[15px] shrink-0 text-text-subtle" aria-hidden />
                )}
              </Link>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
